import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { usePopover } from '../../../hooks/usePopover';
import { cn } from '../../../utils/cn';
import { formatDate, parseISODate, toISODate } from '../../../utils/date';
import { controlClass, describedBy, Field, FieldOwnProps } from '../Field/Field';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Portal } from '../Portal/Portal';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const addMonths = (d, n) => {
  const r = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const last = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate();
  return new Date(r.getFullYear(), r.getMonth(), Math.min(d.getDate(), last));
};
const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * Date field with our own calendar. `value` / `onChange` use "YYYY-MM-DD"
 * strings, which the backend accepts directly.
 */
export interface DatePickerProps extends Omit<FieldOwnProps, 'labelAction'> {
  /** "YYYY-MM-DD" */
  value?: string | null;
  /** "YYYY-MM-DD", or '' when cleared */
  onChange?: (value: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  clearable?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md';
  id?: string;
  'aria-label'?: string;
}

export function DatePicker({
  label, hint, error, required, value, onChange, min, max, placeholder = 'Pick a date',
  clearable = true, disabled, size = 'md', containerClassName, id, 'aria-label': ariaLabel,
}: DatePickerProps) {
  const autoId = useId();
  const btnId = id || autoId;
  const selected = parseISODate(value);
  const minD = parseISODate(min);
  const maxD = parseISODate(max);
  const today = new Date();

  const [open, setOpen] = useState(false);
  const [focusDate, setFocusDate] = useState<Date>(selected || today);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const style = usePopover(open, anchorRef, popRef);
  useClickOutside([anchorRef, popRef], () => setOpen(false), open);

  const isDisabled = (d: Date) => !!((minD && d < minD) || (maxD && d > maxD));

  const days = useMemo(() => {
    const first = new Date(focusDate.getFullYear(), focusDate.getMonth(), 1);
    const start = addDays(first, -((first.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }, [focusDate]);

  // Keep keyboard focus on the highlighted day.
  useEffect(() => {
    if (!open) return;
    const el = gridRef.current?.querySelector<HTMLElement>(`[data-date="${toISODate(focusDate)}"]`);
    el?.focus({ preventScroll: true });
  }, [open, focusDate]);

  const openCalendar = () => {
    if (disabled) return;
    setFocusDate(selected || today);
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    anchorRef.current?.focus();
  };
  const choose = (d) => {
    if (isDisabled(d)) return;
    onChange?.(toISODate(d));
    close();
  };

  const onGridKey = (e) => {
    const moves = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    if (moves[e.key]) {
      e.preventDefault();
      setFocusDate((d) => addDays(d, moves[e.key]));
    } else if (e.key === 'PageUp' || e.key === 'PageDown') {
      e.preventDefault();
      setFocusDate((d) => addMonths(d, e.key === 'PageUp' ? -1 : 1));
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      setFocusDate((d) => addDays(d, e.key === 'Home' ? -((d.getDay() + 6) % 7) : 6 - ((d.getDay() + 6) % 7)));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      choose(focusDate);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  const monthLabel = focusDate.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={btnId} className={containerClassName}>
      <div className="relative">
        <button
          ref={anchorRef}
          id={btnId}
          type="button"
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-describedby={describedBy(btnId, error, hint)}
          disabled={disabled}
          onClick={() => (open ? setOpen(false) : openCalendar())}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && !open) {
              e.preventDefault();
              openCalendar();
            }
          }}
          className={cn(
            controlClass({ error, disabled, open }),
            'flex items-center gap-2.5 pl-3.5 text-left',
            clearable && value ? 'pr-10' : 'pr-3',
            size === 'sm' ? 'h-9 text-sm' : 'h-10 text-sm',
          )}
        >
          <Icon name="calendar" size={18} className="shrink-0 text-ink-400" />
          <span className={cn('flex-1 truncate', !value && 'text-ink-300')}>{value ? formatDate(value) : placeholder}</span>
        </button>
        {clearable && value && !disabled && (
          <button
            type="button"
            aria-label="Clear date"
            onClick={() => onChange?.('')}
            className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-ink-400 hover:bg-navy-50 hover:text-navy-800"
          >
            <Icon name="x" size={16} />
          </button>
        )}
      </div>

      {open && (
        <Portal>
          <div
            ref={popRef}
            style={style}
            role="dialog"
            aria-label="Choose date"
            data-popup
            className="w-[304px] rounded-2xl border border-line bg-white p-3 shadow-pop animate-pop"
          >
            <div className="mb-2 flex items-center justify-between">
              <IconButton size="sm" icon="chevronLeft" label="Previous month" onClick={() => setFocusDate((d) => addMonths(d, -1))} />
              <span className="text-sm font-semibold text-navy-800" aria-live="polite">{monthLabel}</span>
              <IconButton size="sm" icon="chevronRight" label="Next month" onClick={() => setFocusDate((d) => addMonths(d, 1))} />
            </div>
            <div className="grid grid-cols-7 text-center text-xs font-medium text-ink-400">
              {WEEKDAYS.map((w) => (
                <span key={w} className="py-1.5">{w}</span>
              ))}
            </div>
            <div ref={gridRef} role="grid" onKeyDown={onGridKey} className="grid grid-cols-7 gap-0.5">
              {days.map((d) => {
                const iso = toISODate(d);
                const inMonth = d.getMonth() === focusDate.getMonth();
                const isSel = sameDay(d, selected);
                const isToday = sameDay(d, today);
                const off = isDisabled(d);
                return (
                  <button
                    key={iso}
                    type="button"
                    data-date={iso}
                    role="gridcell"
                    aria-selected={isSel}
                    aria-label={d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    tabIndex={sameDay(d, focusDate) ? 0 : -1}
                    disabled={off}
                    onClick={() => choose(d)}
                    className={cn(
                      'tabular relative flex h-10 items-center justify-center rounded-lg text-sm transition-colors outline-none',
                      'focus-visible:ring-2 focus-visible:ring-steel',
                      isSel ? 'bg-brand-600 font-semibold text-white' : 'hover:bg-navy-50',
                      !isSel && (inMonth ? 'text-navy-900' : 'text-ink-300'),
                      !isSel && sameDay(d, focusDate) && 'bg-navy-50',
                      off && 'cursor-not-allowed opacity-30 hover:bg-transparent',
                    )}
                  >
                    {d.getDate()}
                    {isToday && <span className={cn('absolute bottom-1.5 h-1 w-1 rounded-full', isSel ? 'bg-white' : 'bg-brand-600')} />}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-line pt-2">
              <button type="button" disabled={isDisabled(today)} onClick={() => choose(today)} className="rounded-lg px-2 py-1.5 text-sm font-medium text-steel hover:bg-navy-50 disabled:opacity-40">
                Today
              </button>
              {clearable && (
                <button
                  type="button"
                  onClick={() => {
                    onChange?.('');
                    close();
                  }}
                  className="rounded-lg px-2 py-1.5 text-sm font-medium text-ink-500 hover:bg-navy-50"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </Portal>
      )}
    </Field>
  );
}

export default DatePicker;
